// src/OnlineGame/CharacterCreationScene.js
import Phaser from "phaser";

export default class CharacterCreationScene extends Phaser.Scene {
  constructor() {
    super({ key: "CharacterCreationScene" });
  }

  init(data) {
    // Retrieve the chosen class from the previous scene; default to "archer"
    this.chosenClass = data.chosenClass || "archer";
    console.log("CharacterCreationScene - Chosen Class:", this.chosenClass);
  }

  preload() {
    // Load all character idle spritesheets so switching is instant.
    const classes = ["archer", "knight", "orc", "priest", "swordsman", "wizard"];
    classes.forEach(cls => {
      this.load.spritesheet(
        `${cls}-idle`,
        `/assets/chars/${cls}/${cls}-Idle.png`,
        { frameWidth: 100, frameHeight: 100 }
      );
    });
  }

  create() {
    // --- Title ---
    this.add.text(400, 20, "Create Your Character", {
      font: "28px monospace",
      fill: "#ffffff"
    }).setOrigin(0.5);

    // --- Class Selection Row ---
    const classOptions = ["archer", "knight", "orc", "priest", "swordsman", "wizard"];
    this.classOptionTexts = {};
    const spacing = 100;
    const offset = ((classOptions.length - 1) * spacing) / 2;
    const classY = 80;

    classOptions.forEach((cls, index) => {
      const txt = this.add.text(400 - offset + index * spacing, classY, cls.toUpperCase(), {
        font: "20px monospace",
        fill: "#ffff00"
      })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      txt.on("pointerdown", () => {
        console.log("Selected class:", cls);
        this.chosenClass = cls;

        // Update the preview sprite's texture and animation.
        if (this.previewSprite) {
          this.previewSprite.setTexture(`${cls}-idle`);
          if (!this.anims.exists(`${cls}-idle-anim`)) {
            this.anims.create({
              key: `${cls}-idle-anim`,
              frames: this.anims.generateFrameNumbers(`${cls}-idle`, { start: 0, end: 5 }),
              frameRate: 6,
              repeat: -1
            });
          }
          this.previewSprite.play(`${cls}-idle-anim`);
        }

        // Update preset attributes based on chosen class.
        const baseAttributes = {
          archer: { Strength: 5, Dexterity: 15, Agility: 10, Wisdom: 5, Stamina: 5 },
          swordsman: { Strength: 15, Dexterity: 8, Agility: 4, Wisdom: 3, Stamina: 10 },
          orc: { Strength: 20, Dexterity: 4, Agility: 3, Wisdom: 3, Stamina: 10 },
          knight: { Strength: 10, Dexterity: 8, Agility: 4, Wisdom: 3, Stamina: 15 },
          priest: { Strength: 5, Dexterity: 10, Agility: 5, Wisdom: 10, Stamina: 10 },
          wizard: { Strength: 5, Dexterity: 10, Agility: 5, Wisdom: 15, Stamina: 5 }
        };
        const newAttrs = baseAttributes[cls] || baseAttributes.archer;
        for (const attr in newAttrs) {
          if (this.attributeTexts[attr]) {
            this.attributeTexts[attr].value = newAttrs[attr];
            this.attributeTexts[attr].text.setText(newAttrs[attr]);
          }
        }
      });

      this.classOptionTexts[cls] = txt;
    });

    // --- Label for Game ID ---
    this.add.text(150, 150, "Game ID", {
      font: "20px monospace",
      fill: "#ffff00"
    }).setOrigin(0.5);

    // --- DOM Input for Game ID ---
    // We want the input to appear immediately below the label.
    const gameCoordX = 165; // Adjust as needed for proper alignment
    const gameCoordY = -30; // Adjust Y offset to be just below the label
    const rect = this.game.canvas.getBoundingClientRect();
    const pageX = rect.left + gameCoordX;
    const pageY = rect.top + gameCoordY;
    const styleStr = `
      position: absolute;
      left: ${pageX}px;
      top: ${pageY}px;
      font-size: 20px;
      width: 200px;
      padding: 5px;
    `;
    const gameIdInput = this.add.dom(0, 0, "input", styleStr, "Enter Game ID");
    // Note: We pass (0, 0) as the scene coordinates because the position is controlled by CSS.

    // --- Character Preview ---
    // Place the character preview below the input.
    this.previewSprite = this.add.sprite(200, 350, `${this.chosenClass}-idle`).setScale(5);
    const animKey = `${this.chosenClass}-idle-anim`;
    if (!this.anims.exists(animKey)) {
      this.anims.create({
        key: animKey,
        frames: this.anims.generateFrameNumbers(`${this.chosenClass}-idle`, { start: 0, end: 5 }),
        frameRate: 6,
        repeat: -1
      });
    }
    this.previewSprite.play(animKey);

    // --- Right Side: Display Preset Attributes and Redistribution Controls ---
    const baseAttributes = {
      archer: { Strength: 5, Dexterity: 15, Agility: 10, Wisdom: 5, Stamina: 5 },
      swordsman: { Strength: 15, Dexterity: 8, Agility: 4, Wisdom: 3, Stamina: 10 },
      orc: { Strength: 20, Dexterity: 4, Agility: 3, Wisdom: 3, Stamina: 10 },
      knight: { Strength: 10, Dexterity: 8, Agility: 4, Wisdom: 3, Stamina: 15 },
      priest: { Strength: 5, Dexterity: 10, Agility: 5, Wisdom: 10, Stamina: 10 },
      wizard: { Strength: 5, Dexterity: 10, Agility: 5, Wisdom: 15, Stamina: 5 }
    };
    const attributes = baseAttributes[this.chosenClass] || baseAttributes.archer;
    // Start with no available points (0). You can adjust this if you want to give initial points.
    this.availablePoints = 0;
    // Display available points above the attribute column.
    this.pointsLeftText = this.add.text(580, 150, "Points Left: 0", {
      font: "20px monospace",
      fill: "#ffffff"
    }).setOrigin(0.5);

    const attrStartX = 550;
    const attrStartY = 200;
    const attrSpacing = 60;
    this.attributeTexts = {};
    let index = 0;
    for (const attr in attributes) {
      // Attribute label.
      this.add.text(attrStartX, attrStartY + index * attrSpacing, attr, {
        font: "20px monospace",
        fill: "#ffff00"
      }).setOrigin(0.5);
      // Attribute value.
      const valueText = this.add.text(attrStartX, attrStartY + index * attrSpacing + 30, attributes[attr], {
        font: "20px monospace",
        fill: "#ffffff"
      }).setOrigin(0.5);
      this.attributeTexts[attr] = { value: attributes[attr], text: valueText };

      // Minus button.
      const minusButton = this.add.text(attrStartX - 50, attrStartY + index * attrSpacing + 30, "-", {
        font: "28px monospace",
        fill: "#ff0000"
      })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      minusButton.on("pointerdown", () => {
        if (this.attributeTexts[attr].value > 0) {
          this.attributeTexts[attr].value--;
          valueText.setText(this.attributeTexts[attr].value);
          this.availablePoints++;
          this.pointsLeftText.setText("Points Left: " + this.availablePoints);
        }
      });
      // Plus button.
      const plusButton = this.add.text(attrStartX + 50, attrStartY + index * attrSpacing + 30, "+", {
        font: "28px monospace",
        fill: "#00ff00"
      })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      plusButton.on("pointerdown", () => {
        if (this.availablePoints > 0) {
          this.attributeTexts[attr].value++;
          valueText.setText(this.attributeTexts[attr].value);
          this.availablePoints--;
          this.pointsLeftText.setText("Points Left: " + this.availablePoints);
        }
      });
      index++;
    }

    // --- Confirm Button ---
    const confirmButton = this.add.text(400, 550, "Confirm", {
      font: "24px monospace",
      fill: "#00ff00",
      backgroundColor: "#000000",
      padding: { x: 10, y: 5 }
    })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    confirmButton.on("pointerdown", () => {
      // Retrieve the typed Game ID from the DOM input.
      const gameId = gameIdInput.node.value;
      console.log("Character created with Game ID:", gameId);

      for (const attr in this.attributeTexts) {
        console.log(attr, ":", this.attributeTexts[attr].value);
      }

      // Create a character object to save.
      const characterData = {
        chosenClass: this.chosenClass,
        gameId: gameId,
        attributes: Object.fromEntries(
          Object.entries(this.attributeTexts).map(([k, v]) => [k, v.value])
        )
      };

      // Retrieve saved characters array (up to 3) from localStorage.
      let savedChars = JSON.parse(localStorage.getItem("savedCharacters")) || [];
      // Use a provided slotIndex if needed; here we simply push if there are fewer than 3.
      if (savedChars.length < 3) {
        savedChars.push(characterData);
      } else {
        // Optionally, replace the first character if full.
        savedChars[0] = characterData;
      }
      localStorage.setItem("savedCharacters", JSON.stringify(savedChars));
      console.log("Saved Characters:", savedChars);

      // Transition to the main game scene, passing the character data.
      this.scene.start("PhaserMMO", {
        chosenClass: this.chosenClass,
        gameId: gameId,
        attributes: characterData.attributes
      });
    });
  }
}
